/** Direct-to-R2 multipart upload for web (File.slice + PUT). Falls back to proxy. */

const PART_RESUME_PREFIX = 'flea.r2_multipart.';

function loadResume(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveResume(key, state) {
  try {
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* storage full / unavailable — resume is best effort */
  }
}

function clearResume(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function isAbortError(err) {
  return (
    err?.name === 'AbortError'
    || err?.name === 'CanceledError'
    || err?.code === 'ERR_CANCELED'
  );
}

function putPart(url, body, signal) {
  return fetch(url, { method: 'PUT', body, signal }).then((res) => {
    if (!res.ok) {
      throw new Error(`Part upload failed with HTTP ${res.status}`);
    }
    const etag = res.headers.get('ETag') || res.headers.get('etag');
    if (!etag) {
      throw new Error('Part upload missing ETag. Ensure R2 CORS ExposeHeaders includes ETag.');
    }
    return etag.replace(/^"|"$/g, '');
  });
}

/** PUT with byte-level upload progress (fetch cannot report upload progress). */
function putPartWithProgress(url, blob, onBytes, signal) {
  // Send raw bytes with no Content-Type. The presigned upload_part URL is not
  // signed for that header, and fetch/XHR would otherwise add blob.type and
  // R2 would reject every part with 403 SignatureDoesNotMatch.
  return blob.arrayBuffer().then((buf) => {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    if (typeof XMLHttpRequest === 'undefined') {
      return putPart(url, buf, signal).then((etag) => {
        onBytes?.(blob.size);
        return etag;
      });
    }
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const cleanup = () => signal?.removeEventListener('abort', onAbort);
      const onAbort = () => xhr.abort();
      xhr.open('PUT', url);
      if (signal) {
        if (signal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
      }
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onBytes?.(e.loaded);
      };
      xhr.onload = () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag') || '';
          if (!etag) {
            reject(new Error('Part upload missing ETag. Ensure R2 CORS ExposeHeaders includes ETag.'));
            return;
          }
          resolve(String(etag).replace(/^"|"$/g, ''));
        } else {
          reject(new Error(`Part upload failed with HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => {
        cleanup();
        reject(new Error('Part upload failed (network error)'));
      };
      xhr.onabort = () => {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
      };
      xhr.send(buf);
    });
  });
}

/**
 * Abort an in-flight/paused multipart session: clears local resume state and
 * tells the backend to abort the R2 multipart upload (best effort).
 * @param {string} resumeKey - the same resumeKey passed to uploadFileDirectMultipart
 * @param {(payload: {upload_id: string, key: string}) => Promise<any>} [abortFn]
 */
export async function abortMultipartSession(resumeKey, abortFn) {
  const storageKey = `${PART_RESUME_PREFIX}${resumeKey}`;
  const resume = loadResume(storageKey);
  clearResume(storageKey);
  if (resume?.uploadId && resume?.key && abortFn) {
    try {
      await abortFn({ upload_id: resume.uploadId, key: resume.key });
    } catch {
      /* best effort */
    }
  }
}

/**
 * @param {object} options
 * @param {object} options.api - { init, signParts, complete, abort?, proxyUpload }
 * @param {File|Blob} options.file
 * @param {string} options.filename
 * @param {string} [options.contentType]
 * @param {string} options.resumeKey
 * @param {(label: string, meta?: { percent?: number }) => void} [options.onProgress]
 * @param {AbortSignal} [options.signal] - cancels in-flight part PUTs
 * @param {boolean} [options.allowProxyFallback] - default true; when false and the
 *   server reports proxy mode, resolves with { deferred: true } instead of uploading.
 * @returns {Promise<{ remoteImageId?: number, key?: string, deferred?: boolean }>}
 */
export async function uploadFileDirectMultipart({
  api,
  file,
  filename,
  contentType,
  resumeKey,
  onProgress,
  signal,
  allowProxyFallback = true,
}) {
  const sizeBytes = file.size;
  const type = contentType || file.type || 'application/octet-stream';
  const storageKey = `${PART_RESUME_PREFIX}${resumeKey}`;

  const throwIfAborted = () => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  };

  let resume = loadResume(storageKey);
  let uploadId = resume?.uploadId;
  let key = resume?.key;
  let partSize = resume?.partSize || 5 * 1024 * 1024;
  let partCount = resume?.partCount || 0;
  let urls = {};
  const completed = new Map(
    (resume?.completedParts || []).map((p) => [p.PartNumber, String(p.ETag).replace(/^"|"$/g, '')]),
  );

  if (!uploadId || !key) {
    onProgress?.('Starting direct upload…', { percent: 0 });
    const init = await api.init({
      filename,
      content_type: type,
      size_bytes: sizeBytes,
    });
    if (init.mode !== 'direct' || !init.upload_id || !init.key) {
      if (!allowProxyFallback) {
        clearResume(storageKey);
        return { deferred: true };
      }
      onProgress?.('Using server upload…', { percent: 0 });
      const proxied = await api.proxyUpload();
      clearResume(storageKey);
      return { remoteImageId: proxied?.images?.[0]?.id };
    }
    uploadId = init.upload_id;
    key = init.key;
    partSize = init.part_size || partSize;
    partCount = init.part_count || Math.max(1, Math.ceil(sizeBytes / partSize));
    urls = init.urls || {};
    resume = {
      uploadId,
      key,
      partSize,
      partCount,
      contentType: init.content_type || type,
      filename,
      sizeBytes,
      completedParts: [],
    };
    saveResume(storageKey, resume);
  } else {
    partCount = resume.partCount || Math.max(1, Math.ceil(sizeBytes / partSize));
  }

  const partSizeAt = (partNumber) =>
    Math.max(0, Math.min(partSize, sizeBytes - (partNumber - 1) * partSize));
  const loadedByPart = new Map();
  const reportPercent = () => {
    let loaded = 0;
    loadedByPart.forEach((v) => { loaded += v; });
    completed.forEach((_, partNumber) => { loaded += partSizeAt(partNumber); });
    const percent = sizeBytes > 0 ? Math.min(99, Math.round((loaded / sizeBytes) * 100)) : 0;
    onProgress?.(`Uploading… ${percent}%`, { percent });
  };
  // Seed already-completed parts so resumed sessions report correct progress.
  reportPercent();

  for (let partNumber = 1; partNumber <= partCount; partNumber++) {
    throwIfAborted();
    if (completed.has(partNumber)) {
      onProgress?.(`Part ${partNumber}/${partCount} already uploaded`, { percent: undefined });
      continue;
    }
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, sizeBytes);
    if (start >= sizeBytes) break;
    const blob = file.slice(start, end);

    onProgress?.(`Uploading part ${partNumber} of ${partCount}…`);
    let url = urls[String(partNumber)];
    if (!url) {
      const signed = await api.signParts({
        upload_id: uploadId,
        key,
        part_numbers: [partNumber],
      });
      url = signed.urls?.[String(partNumber)];
    }
    if (!url) throw new Error(`Missing presigned URL for part ${partNumber}`);

    const onBytes = (loaded) => {
      loadedByPart.set(partNumber, loaded);
      reportPercent();
    };

    try {
      const etag = await putPartWithProgress(url, blob, onBytes, signal);
      completed.set(partNumber, etag);
    } catch (firstErr) {
      if (isAbortError(firstErr)) throw firstErr;
      const signed = await api.signParts({
        upload_id: uploadId,
        key,
        part_numbers: [partNumber],
      });
      const retryUrl = signed.urls?.[String(partNumber)];
      if (!retryUrl) throw firstErr;
      loadedByPart.delete(partNumber);
      const etag = await putPartWithProgress(retryUrl, blob, onBytes, signal);
      completed.set(partNumber, etag);
    }
    loadedByPart.delete(partNumber);

    resume = {
      ...resume,
      completedParts: Array.from(completed.entries()).map(([PartNumber, ETag]) => ({
        PartNumber,
        ETag,
      })),
    };
    saveResume(storageKey, resume);
    reportPercent();
  }

  throwIfAborted();
  onProgress?.('Finishing upload…', { percent: 100 });
  const parts = Array.from(completed.entries())
    .map(([PartNumber, ETag]) => ({ PartNumber, ETag }))
    .sort((a, b) => a.PartNumber - b.PartNumber);

  const result = await api.complete({
    upload_id: uploadId,
    key,
    parts,
    filename,
  });
  clearResume(storageKey);
  return { remoteImageId: result?.images?.[0]?.id, key: result?.key || key };
}
